import React, { useEffect, useRef } from 'react';
import { createChart, ColorType } from 'lightweight-charts';
import * as LightweightCharts from 'lightweight-charts';

/**
 * TradingChart
 *
 * Props:
 *   symbol    Binance pair like "BTCUSDT". When provided, the component
 *             fetches historical klines from Binance REST and streams live
 *             updates via `wss://stream.binance.com:9443/ws/{symbol}@kline_{interval}`.
 *   interval  '1m' | '5m' | '1h' | '1d' (default '1m')
 *   data      Optional static candle array. Used only when `symbol` is not
 *             provided (kept for backwards compat with sample-data callers).
 *
 * Each Binance kline message looks like:
 *   { e:'kline', k:{ t, T, s, i, o, c, h, l, v, x, ... } }
 * `t` is open-time ms, `x` is "is this kline closed?".
 */

const KLINES_URL = 'https://api.binance.com/api/v3/klines';
const WS_BASE = 'wss://stream.binance.com:9443/ws';
const KLINE_LIMIT = 500;

const TradingChart = ({ symbol, interval = '1m', data, pair }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const aliveRef = useRef(true);

  // Build the chart once; subsequent prop changes are pushed into the series.
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#000000',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' },
      },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#d1d4dc',
      },
      rightPriceScale: { borderColor: '#d1d4dc' },
      crosshair: { mode: 1 },
    });

    const candleSeries = chart.addSeries(LightweightCharts.CandlestickSeries, {
      upColor: '#0F4C5C',
      downColor: '#FF5A5A',
      borderUpColor: '#0F4C5C',
      borderDownColor: '#FF5A5A',
      wickUpColor: '#0F4C5C',
      wickDownColor: '#FF5A5A',
    });

    const volumeSeries = chart.addSeries(LightweightCharts.HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: '',
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const handleResize = () => {
      if (!containerRef.current || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  // (Re)load data and (re)subscribe to live updates whenever symbol/interval
  // change. The static `data` prop is honoured only when no symbol is given.
  useEffect(() => {
    aliveRef.current = true;
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    if (!candleSeries || !volumeSeries) return undefined;

    const teardownLive = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.onclose = null;
          wsRef.current.onerror = null;
          wsRef.current.onmessage = null;
          wsRef.current.close();
        } catch (e) {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
    teardownLive();

    if (!symbol) {
      const initial = data && data.length ? data : generateSampleData();
      candleSeries.setData(initial);
      volumeSeries.setData(
        initial.map((c) => ({
          time: c.time,
          value: c.volume ?? Math.random() * 1000000,
          color: c.close > c.open ? '#0F4C5C50' : '#FF5A5A50',
        }))
      );
      chartRef.current?.timeScale().fitContent();
      return () => {
        aliveRef.current = false;
      };
    }

    const normSymbol = String(symbol).toUpperCase();
    const wsSymbol = normSymbol.toLowerCase();
    const wsUrl = `${WS_BASE}/${wsSymbol}@kline_${interval}`;

    // 1. Historical seed from Binance REST.
    const url = `${KLINES_URL}?symbol=${normSymbol}&interval=${interval}&limit=${KLINE_LIMIT}`;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`klines ${r.status}`);
        return r.json();
      })
      .then((rows) => {
        if (!aliveRef.current) return;
        const candles = [];
        const volumes = [];
        for (const row of rows) {
          const [openTime, o, h, l, c, v] = row;
          const candle = {
            time: Math.floor(openTime / 1000),
            open: parseFloat(o),
            high: parseFloat(h),
            low: parseFloat(l),
            close: parseFloat(c),
          };
          candles.push(candle);
          volumes.push({
            time: candle.time,
            value: parseFloat(v),
            color: candle.close > candle.open ? '#0F4C5C50' : '#FF5A5A50',
          });
        }
        candleSeries.setData(candles);
        volumeSeries.setData(volumes);
        chartRef.current?.timeScale().fitContent();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Binance klines fetch failed:', err);
      });

    // 2. Live updates over WS with exponential-backoff reconnect.
    let backoff = 1000;

    const scheduleReconnect = () => {
      if (!aliveRef.current) return;
      reconnectTimerRef.current = setTimeout(() => {
        backoff = Math.min(backoff * 2, 30000);
        connect();
      }, backoff);
    };

    const connect = () => {
      if (!aliveRef.current) return;
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('WS construct failed:', e);
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        backoff = 1000;
      };

      ws.onmessage = (event) => {
        if (!aliveRef.current) return;
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch (e) {
          return;
        }
        const k = msg && msg.k;
        if (!k) return;
        const time = Math.floor(k.t / 1000);
        const open = parseFloat(k.o);
        const high = parseFloat(k.h);
        const low = parseFloat(k.l);
        const close = parseFloat(k.c);
        const volume = parseFloat(k.v);
        try {
          candleSeriesRef.current?.update({ time, open, high, low, close });
          volumeSeriesRef.current?.update({
            time,
            value: volume,
            color: close > open ? '#0F4C5C50' : '#FF5A5A50',
          });
        } catch (e) {
          // Out-of-order frame after a reconnect — ignore.
        }
      };

      ws.onerror = () => {
        /* browser will fire onclose next */
      };

      ws.onclose = () => {
        if (!aliveRef.current) return;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      aliveRef.current = false;
      teardownLive();
    };
  }, [symbol, interval, data]);

  return <div ref={containerRef} data-pair={pair} className="w-full h-full" />;
};

const generateSampleData = () => {
  const out = [];
  const now = Math.floor(Date.now() / 1000);
  let price = 86500;
  for (let i = 300; i >= 0; i--) {
    const open = price;
    const close = open + (Math.random() - 0.5) * 200;
    const high = Math.max(open, close) + Math.random() * 100;
    const low = Math.min(open, close) - Math.random() * 100;
    out.push({ time: now - i * 300, open, high, low, close });
    price = close;
  }
  return out;
};

export default TradingChart;
